import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchPublishedNews, type NewsEntry } from "@/features/news/news";
import { youtubeThumbnail } from "@/shared/utils/youtube";

/** Sección "Lo último": novedades publicadas (desde la BD vía API). */
export function News() {
  const [posts, setPosts] = useState<NewsEntry[]>([]);

  useEffect(() => {
    let active = true;
    fetchPublishedNews({ limit: 3 })
      .then((d) => active && setPosts(d))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (posts.length === 0) return null;

  return (
    <section id="novedades" className="py-24 lg:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 lg:mb-16 gap-4 reveal">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Lo último</span>
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mt-3">Novedades y casos de éxito.</h2>
          </div>
          <Link to="/lo-ultimo" className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary">
            Ver todo <ArrowUpRight className="size-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {posts.map((p, i) => {
            const cover = p.image || youtubeThumbnail(p.youtubeUrl);
            const tag = p.categories[0];
            return (
              <Link
                key={p.id}
                to={`/lo-ultimo/${p.id}`}
                className="group bg-white p-6 lg:p-7 rounded-3xl border border-black/5 hover:shadow-xl hover:-translate-y-1 transition-all reveal block"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center justify-between mb-6">
                  {tag ? (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-full">{tag}</span>
                  ) : <span />}
                  <span className="text-xs text-muted-foreground">{p.date}</span>
                </div>
                <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-6 relative bg-gradient-to-br from-neutral-100 via-neutral-50 to-primary/5">
                  {cover ? (
                    <img src={cover} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center text-3xl font-bold text-neutral-300">{(tag ?? p.title)[0]}</div>
                  )}
                </div>
                <h3 className="text-lg font-bold leading-snug mb-2 group-hover:text-primary transition-colors">{p.title}</h3>
                <p className="text-sm text-muted-foreground">{p.excerpt}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
